<template>
  <div class="ag-studio">
    <div v-if="pageLoading" class="ag-studio__loading">
      <v-progress-circular indeterminate color="primary" size="32" />
    </div>

    <template v-else>
      <div class="ag-studio__panel">
        <AgentCommandCenter
          v-model="activeTab"
          :tabs="studioTabs"
          :show-train-knowledge="activeTab === 'knowledge-base'"
          :is-session-ready="isSessionReady"
          @train="openTrainKnowledgeDialog"
        />

        <div class="ag-studio__workspace">
          <main class="ag-studio__main">
            <div class="ag-studio__content ag-studio__content--fill">
              <AgentKnowledgeStudio
                v-if="activeTab === 'knowledge-base'"
                v-model:show-train-dialog="showTrainDialog"
                :active="true"
              />
              <!-- Temporarily hidden — re-enable when Conversation blocks is ready for release -->
              <!--
              <ConversationRulesTab
                v-if="activeTab === 'conversation-rules'"
                :active="true"
                @counts-changed="refreshCounts"
              />
              <PostCallActionsTab
                v-if="activeTab === 'post-call-actions'"
                :active="true"
                @counts-changed="refreshCounts"
              />
              -->
              <IntegrationsTab
                v-else-if="activeTab === 'integrations'"
                :active="true"
                @counts-changed="refreshCounts"
              />
              <AgentSettingsStudio
                v-else-if="activeTab === 'other-settings'"
                :active="true"
              />
            </div>
          </main>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import AgentCommandCenter from '@/components/Agent/Studio/AgentCommandCenter.vue'
import AgentKnowledgeStudio from '@/components/Agent/Studio/AgentKnowledgeStudio.vue'
import AgentSettingsStudio from '@/components/Agent/Studio/AgentSettingsStudio.vue'
// Temporarily hidden — re-enable with Conversation blocks & Post call actions tabs
// import ConversationRulesTab from '@/components/Organizations/Tabs/ConversationRulesTab.vue'
// import PostCallActionsTab from '@/components/Organizations/Tabs/PostCallActionsTab.vue'
import IntegrationsTab from '@/components/Agent/OnCall/IntegrationsTab.vue'
import { useAgentTabCounts } from '@/composables/agent/useAgentTabCounts'
import { loadOrgDetailsIntoStore } from '@/utils/orgDetailsLoader'

defineOptions({ name: 'KnowledgeBasePage' })

const store = useStore()
const activeTab = ref('knowledge-base')
const showTrainDialog = ref(false)

function openTrainKnowledgeDialog() {
  showTrainDialog.value = false
  nextTick(() => {
    showTrainDialog.value = true
  })
}

const pageLoading = computed(
  () =>
    store.state.sessionLoading ||
    !store.getters.isSessionReady ||
    store.state.orgDetailsLoading,
)

const isSessionReady = computed(() => store.getters.isSessionReady)

const { refreshCounts } = useAgentTabCounts(isSessionReady)

const studioTabs = [
  {
    value: 'knowledge-base',
    title: 'Knowledge base',
    icon: 'mdi-book-open-page-variant-outline',
  },
  // Temporarily hidden — re-enable when ready for release
  // {
  //   value: 'conversation-rules',
  //   title: 'Conversation blocks',
  //   icon: 'mdi-message-cog-outline',
  // },
  // {
  //   value: 'post-call-actions',
  //   title: 'Post call actions',
  //   icon: 'mdi-lightning-bolt-outline',
  // },
  {
    value: 'integrations',
    title: 'Integrations',
    icon: 'mdi-puzzle-outline',
  },
  {
    value: 'other-settings',
    title: 'Agent settings',
    icon: 'mdi-tune-variant',
  },
]

async function refreshOrgDetails() {
  if (!isSessionReady.value) return
  await loadOrgDetailsIntoStore(store)
}

onMounted(() => {
  void refreshOrgDetails()
})

watch(isSessionReady, (ready) => {
  if (ready) void refreshOrgDetails()
})

watch(activeTab, (tab) => {
  if (tab === 'knowledge-base') void refreshOrgDetails()
})
</script>
